import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useGetCredit from '@webclient/hooks/useGetCredit';
import useUpgradeAccountModal from '@webclient/hooks/useUpgradeAccountModal';

import { sendMessageToRoom } from '@webclient/state/slices/search-session';
import searchAsyncOrchestrator from '@webclient/state/slices/search-session/thunks/searchAsyncOrchestrator';

import paths from '@webclient/router/paths';

type Props =
  | {
      requestOrigin: 'search';
      searchThreadId: string;
    }
  | {
      requestOrigin: 'home';
    };

const DEFAULT_TIMEOUT = 10000;
const RETRY_INTERVAL = 500;
const OUT_OF_CREDITS_RATIO = 10;

export default function usePollingSearchAsyncOrchestrator(props: Props) {
  const [shouldNavigateTo, setShouldNavigateTo] = useState(false);
  const threadId = useAppSelector((state) => state.searchSession.threadId);
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState(false);
  const { creditsData } = useGetCredit();
  const dispatch = useAppDispatch();
  const searchStatus = useAppSelector(
    (state) => state.searchSession.searchThreadStatus,
  );
  const isConnectedToRoom = useAppSelector(
    (state) => state.searchSession.isConnected,
  );

  const navigateTo = useNavigate();
  const { openUpgradeAccount } = useUpgradeAccountModal();
  const searchStatusRef = useRef(searchStatus);
  const isConnectedToRoomRef = useRef(isConnectedToRoom);

  useEffect(() => {
    searchStatusRef.current = searchStatus;
    isConnectedToRoomRef.current = isConnectedToRoom;
  }, [searchStatus, isConnectedToRoom]);

  useEffect(() => {
    if (!shouldNavigateTo || !threadId) return;
    navigateTo(paths.search.pathname.replace(':id', threadId));
  }, [navigateTo, searchStatus, shouldNavigateTo, threadId]);

  const waitForSearchAndRoomConnectionToComplete = useCallback(
    async (timeout = DEFAULT_TIMEOUT) => {
      const startTime = Date.now();
      while (
        searchStatusRef.current === 'pending' ||
        !isConnectedToRoomRef.current
      ) {
        if (Date.now() - startTime > timeout) {
          throw new Error('Connection timeout');
        }
        setPolling(true);
        await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL));
      }
    },
    [],
  );

  const onSubmit = useCallback(
    async ({ prompt }: { prompt: string }) => {
      if (
        creditsData?.general &&
        creditsData?.general.quantity < OUT_OF_CREDITS_RATIO
      ) {
        openUpgradeAccount();
        return;
      }
      try {
        await waitForSearchAndRoomConnectionToComplete();
        dispatch(sendMessageToRoom(prompt));
        if (props.requestOrigin === 'home') {
          setShouldNavigateTo(true);
        }
      } catch (error) {
        console.error('Error during search and room connection:', error);
      } finally {
        setPolling(false);
      }
    },
    [
      creditsData?.general,
      dispatch,
      openUpgradeAccount,
      props.requestOrigin,
      waitForSearchAndRoomConnectionToComplete,
    ],
  );

  const createSearchAndConnectToRoom = useCallback(
    async ({ withAudio = false }: { withAudio?: boolean }) => {
      setLoading(true);

      return await dispatch(
        searchAsyncOrchestrator(
          props.requestOrigin === 'home'
            ? {
                requestOrigin: 'home',
                withAudio,
              }
            : {
                requestOrigin: 'search',
                searchThreadId: props.searchThreadId,
                withAudio,
              },
        ),
      ).finally(() => setLoading(false));
    },
    [dispatch, props],
  );

  return {
    onSubmit,
    createSearchAndConnectToRoom,
    polling,
    loading,
  };
}
